import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  Credentials,
  List,
  ListItem,
  SidebarBottom,
  SidebarCenter,
  SidebarContainer,
  SidebarTop,
} from "./style";
import BarChartIcon from "@mui/icons-material/BarChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BallotIcon from "@mui/icons-material/Ballot";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import EventIcon from "@mui/icons-material/Event";
import PinDropIcon from "@mui/icons-material/PinDrop";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ContactsIcon from "@mui/icons-material/Contacts";
import ViewListIcon from "@mui/icons-material/ViewList";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import AssessmentIcon from "@mui/icons-material/Assessment";

import { AuthContext } from "../../context/AuthContext";
import { isAdmin, canAccessMagazine, canAccessOpsNav, canAccessCompanies, canEditContacts, canViewMagazineReports, canViewSalesReports } from "../../util/roles";
import { getVisibleNav, NavIcon } from "./navConfig";
import { hasMenuAccess } from "../../util/menuAccess";

const SIDEBAR_SCROLL_KEY = "sidebarScrollTop";

const isNavActive = (
  pathname: string,
  to: string,
  end = false,
  activeExclude: string[] = []
) => {
  const excluded = activeExclude.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  if (excluded) return false;

  if (end || to === "/") {
    return pathname === to;
  }
  return pathname === to || pathname.startsWith(`${to}/`);
};

const NAV_ICONS: Record<NavIcon, React.ReactNode> = {
  statistics: <BarChartIcon className="icon" />,
  dashboard: <DashboardIcon className="icon" />,
  event: <EventIcon className="icon" />,
  map: <PinDropIcon className="icon" />,
  business: <BusinessIcon className="icon" />,
  contacts: <ContactsIcon className="icon" />,
  exhibitors: <AssignmentIcon className="icon" />,
  products: <BallotIcon className="icon" />,
  orders: <ShoppingCartIcon className="icon" />,
  publications: <MenuBookIcon className="icon" />,
  issues: <AutoStoriesIcon className="icon" />,
  inventory: <Inventory2Icon className="icon" />,
  magazineBookings: <BookOnlineIcon className="icon" />,
  allBookings: <ViewListIcon className="icon" />,
  contractTemplates: <DescriptionIcon className="icon" />,
  contracts: <ArticleIcon className="icon" />,
  invoices: <ReceiptLongIcon className="icon" />,
  reports: <AssessmentIcon className="icon" />,
  profile: <AccountBoxIcon className="icon" />,
  colleagues: <GroupWorkIcon className="icon" />,
  permissions: <VerifiedUserIcon className="icon" />,
  system: <HealthAndSafetyIcon className="icon" />,
  settings: <SettingsSuggestIcon className="icon" />,
  logs: <TextSnippetIcon className="icon" />,
};

type NavItemProps = {
  to: string;
  end?: boolean;
  activeExclude?: string[];
  icon: NavIcon;
  label: string;
};

const NavItem = ({ to, end, activeExclude, icon, label }: NavItemProps) => {
  const { pathname } = useLocation();
  const active = isNavActive(pathname, to, end, activeExclude);

  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <ListItem $active={active}>
        {NAV_ICONS[icon]}
        <span>{label}</span>
      </ListItem>
    </Link>
  );
};

const Sidebar = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const admin = isAdmin(user);
  const magazineAccess = canAccessMagazine(user);
  const hasExplicitOpsMenuAccess =
    Array.isArray(user?.menu_items) &&
    user.menu_items.some((key: unknown) =>
      typeof key === "string" && key.startsWith("events.")
    );
  const showOpsNav = canAccessOpsNav(user) || hasExplicitOpsMenuAccess;

  const companiesAccess = canAccessCompanies(user);
  const contactsAccess = canEditContacts(user);
  const magazineReports = canViewMagazineReports(user);
  const salesReports = canViewSalesReports(user);

  const navEntries = useMemo(
    () => getVisibleNav({ admin, showOpsNav, magazineAccess, companiesAccess, contactsAccess, magazineReports, salesReports, menuAccess: (key) => hasMenuAccess(user, key) }),
    [admin, showOpsNav, magazineAccess, companiesAccess, contactsAccess, magazineReports, salesReports, user]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved) {
      el.scrollTop = Number(saved);
    }

    const onScroll = () => {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(el.scrollTop));
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const logout = async () => {
    if (dispatch) dispatch({ type: "LOGOUT", payload: null });
    navigate("/login");
  };

  return (
    <SidebarContainer>
      <SidebarTop>
        <Link to="/dashboard" style={{ textDecoration: "none" }}>
          <div className="logo">{"TORCH"}</div>
        </Link>
      </SidebarTop>
      <SidebarCenter ref={scrollRef}>
        <List>
          {navEntries.map((entry, index) => {
            if (entry.type === "section") {
              return (
                <h2 key={`section-${entry.title}-${index}`} className="itemGroupTitle">
                  {entry.title}
                </h2>
              );
            }
            if (entry.type === "item") {
              return (
                <NavItem
                  key={entry.to}
                  to={entry.to}
                  end={entry.end}
                  activeExclude={entry.activeExclude}
                  icon={entry.icon}
                  label={entry.label}
                />
              );
            }
            return (
              <ListItem key={`static-${entry.label}`}>
                {NAV_ICONS[entry.icon]}
                <span>{entry.label}</span>
              </ListItem>
            );
          })}

          <ListItem />

          <ListItem onClick={logout}>
            <LogoutIcon className="icon" />
            <span>Log Out</span>
          </ListItem>
        </List>
      </SidebarCenter>
      <SidebarBottom>
        <Credentials>
          <p>@TORCH TEAM</p>
          <p>2023</p>
        </Credentials>
      </SidebarBottom>
    </SidebarContainer>
  );
};

export default Sidebar;
